/*
 * Section admin : mode maintenance.
 * Interrupteur qui met le site public en pause pendant une intervention :
 * les visiteuses voient une jolie page d'attente avec un message et une
 * heure estimée de retour personnalisables. L'espace admin reste accessible.
 * L'application est immédiate et un aperçu plein écran est disponible sans
 * couper le site.
 */

import { useCallback, useEffect, useState } from "react";
import { Construction, Eye, Users, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { adminUpdateSettings, adminGetMaintenanceSubscribers } from "@/lib/api";
import { MaintenancePage } from "@/pages/MaintenancePage";

const sectionClass =
  "rounded-2xl bg-white border border-[rgba(35,82,119,0.14)] p-6 shadow-[var(--shadow-sm)] space-y-4";

/* ISO UTC vers valeur d'un champ datetime-local (heure locale). */
const isoToLocalInput = (iso) => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/* Valeur d'un champ datetime-local vers ISO UTC. */
const localInputToIso = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString();
};

/* ----- Champs de reglage : message et heure estimee de retour ----- */

const SettingsFields = ({ message, onMessageChange, resumeLocal, onResumeChange }) => (
  <>
    <div>
      <Label htmlFor="maintenance-message" className="text-sm font-medium text-[#060606]">
        Message affiché aux visiteuses
      </Label>
      <Textarea
        id="maintenance-message"
        data-testid="admin-maintenance-message-input"
        value={message}
        onChange={onMessageChange}
        maxLength={300}
        rows={3}
        placeholder="CYCLIQ est en pause quelques instants pour une amélioration. Reviens très vite, tout sera prêt."
        className="mt-1.5 rounded-xl border-[rgba(35,82,119,0.18)] text-sm"
      />
    </div>

    <div>
      <Label htmlFor="maintenance-resume" className="text-sm font-medium text-[#060606]">
        Heure estimée de retour (facultatif)
      </Label>
      <p className="mt-0.5 text-[11px] text-[#9E9E9E]">
        Un compte à rebours doux s'affiche sur la page de pause. Laisse vide pour ne rien afficher.
      </p>
      <Input
        id="maintenance-resume"
        data-testid="admin-maintenance-resume-input"
        type="datetime-local"
        value={resumeLocal}
        onChange={onResumeChange}
        className="mt-1.5 max-w-[260px] rounded-xl border-[rgba(35,82,119,0.18)] text-sm"
      />
    </div>
  </>
);

/* ----- Apercu plein ecran de la page de pause (sans couper le site) ----- */

const PreviewOverlay = ({ message, resumeAt, logoUrl, onClose }) => (
  <div
    className="fixed inset-0 z-[200] overflow-y-auto bg-[#F9FEFF]"
    data-testid="admin-maintenance-preview-overlay"
    role="dialog"
    aria-label="Aperçu de la page de pause"
  >
    <div className="sticky top-0 z-10 flex items-center justify-between gap-3 bg-[#235277] px-4 py-2.5">
      <p className="text-xs font-semibold text-white">
        Aperçu de la page de pause. Le site public n'est pas coupé.
      </p>
      <Button
        type="button"
        size="sm"
        onClick={onClose}
        data-testid="admin-maintenance-preview-close-button"
        className="min-h-[32px] rounded-lg bg-[#EAFE63] text-[#060606] hover:bg-[#DFF64F] text-xs font-semibold transition-colors duration-200"
      >
        <X className="mr-1 w-3.5 h-3.5" aria-hidden="true" />
        Fermer l'aperçu
      </Button>
    </div>
    <MaintenancePage
      message={message}
      resumeAt={resumeAt}
      logoUrl={logoUrl}
      onRetry={() => {}}
      checking={false}
      previewMode
    />
  </div>
);

export const MaintenanceSection = ({ adminKey, maintenance, logoUrl }) => {
  const [enabled, setEnabled] = useState(Boolean(maintenance?.enabled));
  const [message, setMessage] = useState(maintenance?.message || "");
  const [resumeLocal, setResumeLocal] = useState(isoToLocalInput(maintenance?.resume_at));
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [waitingCount, setWaitingCount] = useState(0);

  const loadWaitingCount = useCallback(async () => {
    try {
      const data = await adminGetMaintenanceSubscribers(adminKey);
      setWaitingCount(data.count || 0);
    } catch (e) {
      // Silencieux : le compteur est purement informatif.
    }
  }, [adminKey]);

  useEffect(() => {
    loadWaitingCount();
  }, [loadWaitingCount]);

  useEffect(() => {
    setEnabled(Boolean(maintenance?.enabled));
    setMessage(maintenance?.message || "");
    setResumeLocal(isoToLocalInput(maintenance?.resume_at));
  }, [maintenance]);

  const apply = async (nextEnabled, nextMessage, nextResumeLocal) => {
    setSaving(true);
    try {
      const result = await adminUpdateSettings(adminKey, {
        maintenance: {
          enabled: nextEnabled,
          message: nextMessage,
          resume_at: localInputToIso(nextResumeLocal),
        },
      });
      const applied = result?.maintenance || {};
      setEnabled(Boolean(applied.enabled));
      setMessage(applied.message || "");
      setResumeLocal(isoToLocalInput(applied.resume_at));
      if (nextEnabled) {
        toast.success("Mode maintenance activé", {
          description: "Les visiteuses voient maintenant la page de pause.",
        });
      } else {
        toast.success("Mode maintenance désactivé", {
          description: "Le site public est de nouveau accessible.",
        });
      }
      return true;
    } catch (error) {
      toast.error("Application impossible", {
        description: "Le changement n'a pas été pris en compte. Réessaie.",
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (value) => {
    const next = Boolean(value);
    setEnabled(next);
    const ok = await apply(next, message, resumeLocal);
    if (!ok) setEnabled(!next);
    else loadWaitingCount();
  };

  const saveDetails = async () => {
    const ok = await apply(enabled, message, resumeLocal);
    if (ok && !enabled) {
      toast.info("Réglages enregistrés", {
        description: "Ils seront appliqués à la prochaine activation du mode.",
      });
    }
  };

  return (
    <section className={sectionClass} data-testid="admin-maintenance-section">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[#235277]">
            <Construction className="w-4 h-4" aria-hidden="true" />
            <h2 className="font-heading text-base font-semibold text-[#060606]">
              Mode maintenance
            </h2>
          </div>
          <p className="mt-1 text-xs leading-relaxed text-[#9E9E9E]">
            Met le site public en pause pendant une intervention : les visiteuses
            voient une page d'attente élégante. Cet espace admin reste accessible.
            L'application est immédiate.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="maintenance-enabled" className="text-xs font-medium text-[#333333]">
            {enabled ? "Activé" : "Désactivé"}
          </Label>
          <Switch
            id="maintenance-enabled"
            data-testid="admin-maintenance-switch"
            checked={enabled}
            disabled={saving}
            onCheckedChange={toggle}
            className="data-[state=checked]:bg-[#D74843]"
          />
        </div>
      </div>

      {enabled && (
        <div
          className="rounded-xl bg-[#FBEBEA] border border-[rgba(140,47,43,0.25)] px-4 py-3"
          data-testid="admin-maintenance-active-warning"
        >
          <p className="text-xs font-medium text-[#8C2F2B]">
            Le site public est actuellement en pause. Les visiteuses ne peuvent pas
            commencer de diagnostic tant que le mode est activé.
          </p>
        </div>
      )}

      {waitingCount > 0 && (
        <div
          className="rounded-xl border border-[rgba(35,82,119,0.14)] bg-[rgba(226,246,255,0.45)] px-4 py-3 flex items-center gap-2"
          data-testid="admin-maintenance-waiting-count"
        >
          <Users className="w-4 h-4 text-[#235277]" aria-hidden="true" />
          <p className="text-xs text-[#333333]">
            <strong>{waitingCount}</strong>{" "}
            {waitingCount > 1 ? "personnes attendent" : "personne attend"} d'être prévenue du
            retour. L'email partira automatiquement dès la désactivation, puis les adresses
            seront supprimées.
          </p>
        </div>
      )}

      <SettingsFields
        message={message}
        onMessageChange={(e) => setMessage(e.target.value)}
        resumeLocal={resumeLocal}
        onResumeChange={(e) => setResumeLocal(e.target.value)}
      />

      <div className="flex flex-wrap items-center justify-end gap-3 pt-1">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setPreviewOpen(true)}
          data-testid="admin-maintenance-preview-button"
          className="min-h-[36px] rounded-lg border-[rgba(35,82,119,0.3)] text-[#235277] hover:bg-[#E2F6FF] text-xs font-medium transition-colors duration-200"
        >
          <Eye className="mr-1.5 w-3.5 h-3.5" aria-hidden="true" />
          Prévisualiser la page de pause
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={saveDetails}
          disabled={saving}
          data-testid="admin-maintenance-save-message-button"
          className="min-h-[36px] rounded-lg border-[rgba(35,82,119,0.3)] text-[#235277] hover:bg-[#E2F6FF] text-xs font-medium transition-colors duration-200"
        >
          {saving ? "Application en cours" : "Enregistrer les réglages"}
        </Button>
      </div>

      {previewOpen && (
        <PreviewOverlay
          message={message}
          resumeAt={localInputToIso(resumeLocal)}
          logoUrl={logoUrl}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </section>
  );
};
